import {
  AbstractControl,
  FormGroup,
  ValidatorFn
} from '@angular/forms';

import {
  Component,
  ElementRef,
  HostBinding,
  TemplateRef,
  Type,
  effect,
  inject,
  input,
  output,
  signal,
  viewChild
} from '@angular/core';

import { BaseDataService } from '@common/services/base-data.service';
import { RecordFormDialogService } from '@common/services/record-form-dialog.service';

@Component({
  template: ''
})
export class FieldBase<T> {
  readonly setFocus = signal(false);

  readonly form = input.required<FormGroup>();

  readonly created = output<ElementRef>();
  readonly next = output<any>();

  readonly field = viewChild<ElementRef>('field');

  protected readonly recordForm = inject(RecordFormDialogService);

  get canCreateRecord(): boolean {
    return this.showInsert && this.recordForm.canOpen(this.dataService);
  }

  protected async createRecord(): Promise<Record<string, unknown> | undefined> {
    if (this.dataService === undefined) return undefined;
    const result = await this.recordForm.openNew(this.dataService, this.label);
    const keyName: string = this.keyField ?? this.dataService.primaryKey;
    const record = result.record;
    if (!result.saved || record === undefined) return undefined;
    return record[keyName] === undefined || record[keyName] === null ? undefined : record;
  }

  fieldClass = '';

  @HostBinding('class')
  get FieldClass(): string {
    return this.fieldClass;
  }

  constructor() {
    effect(() => {
      const element = this.field();

      if (element) {
        this.created.emit(element);
      }
    });
  }

  errors!: any;

  adjustTypeSel!: string;
  autoCompleteCallback!: any;
  autocomplete!: string;
  checkingIfExists!: any;
  columns!: number;
  controlType!: string;
  customizedItems!: any | undefined;
  dataService!: BaseDataService | undefined;
  defaultValue!: T | undefined;
  editFunction!: (row: T) => T;
  email!: boolean;
  explanation!: any;
  fieldName!: any;
  formatFunction!: (row: T) => T;
  formFormat!: string;
  formFormatObject!: boolean;
  fractionDigits!: number;
  fullWidth!: boolean;
  icon!: string | undefined;
  tip!: string | undefined;
  inputMode!: string | undefined;
  autoCapitalize!: string | undefined;
  autoCorrect!: string | undefined;
  insertComponent!: Type<any>;
  key!: string;
  keyField!: any;
  knowErrors!: string[] | [];
  labelClass!: string;
  styleClass!: string;
  subType!: string;
  suffix!: string;
  tabindex!: number;
  textAlign!: string;
  label!: string;
  mask!: string;
  max!: number;
  maxLength!: number;
  min!: number;
  minFractionDigits!: number;
  maxFractionDigits: number | undefined;
  minLength!: number;
  options!: any;
  parentValueField!: string;
  pattern!: string;
  placeholder!: string;
  prefix!: string;
  required!: boolean;
  rows!: number;
  showButtons!: boolean;
  showInsert!: boolean;
  startFocus!: boolean;
  template!: TemplateRef<any> | undefined;
  type!: any;
  uri!: string;
  validators!: ValidatorFn[] | undefined;
  value!: T | any;
  valueField!: any;

  filterFunction:
    | ((items: any[]) => any[])
    | undefined;

  setOptions(options: any): void {
    this.adjustTypeSel =
      options.adjustTypeSel ?? 'R$';

    this.autocomplete =
      options.autocomplete ?? undefined;

    this.autoCompleteCallback =
      options.autoCompleteCallback ?? undefined;

    this.checkingIfExists =
      options.checkingIfExists;

    this.columns =
      options.columns ?? 12;

    this.controlType =
      options.controlType;

    this.customizedItems =
      options.customizedItems ?? undefined;

    this.dataService =
      options.dataService ?? undefined;
    this.dataService?.ensureLoaded();

    this.defaultValue =
      options.defaultValue ?? undefined;

    this.editFunction =
      options.editFunction ?? undefined;

    this.email =
      options.email ?? false;

    this.explanation =
      options.explanation;

    this.fieldName =
      options.fieldName ?? options.key;

    this.formatFunction =
      options.formatFunction ?? undefined;

    this.formFormat =
      options.formFormat ?? '';

    this.formFormatObject =
      options.formFormatObject ?? false;

    this.fractionDigits =
      options.fractionDigits ?? 2;

    this.fullWidth =
      options.fullWidth ?? true;

    this.icon =
      options.icon ?? undefined;

    this.tip =
      options.tip ?? undefined;

    this.inputMode =
      options.inputMode ?? undefined;

    this.autoCapitalize =
      options.autoCapitalize ?? undefined;

    this.autoCorrect =
      options.autoCorrect ?? undefined;

    this.insertComponent =
      options.insertComponent ?? undefined;

    this.key =
      options.key;

    this.keyField =
      options.keyField ?? undefined;

    this.knowErrors =
      options.knowErrors ?? [];

    this.labelClass =
      options.labelClass ?? '';

    this.label =
      options.label;

    this.mask =
      options.mask;

    this.max =
      options.max;

    this.maxLength =
      options.maxLength ?? -1;

    this.min =
      options.min;

    this.minFractionDigits =
      options.minFractionDigits ?? 1;

    this.maxFractionDigits =
      options.maxFractionDigits;

    this.minLength =
      options.minLength ?? -1;

    this.options =
      options.options ?? [];

    this.parentValueField =
      options.parentValueField ?? '';

    this.pattern =
      options.pattern;

    this.placeholder =
      options.placeholder ?? this.label;

    this.prefix =
      options.prefix ?? 'R$ ';

    this.required =
      !!options.required;

    this.rows =
      options.rows ?? 2;

    this.startFocus =
      options.startFocus ?? false;

    this.showButtons =
      options.showButtons ?? false;

    this.showInsert =
      options.showInsert ?? false;

    this.suffix =
      options.suffix;

    this.template =
      options.template ?? undefined;

    this.type =
      options.type ?? 'text';

    this.uri =
      options.uri ?? '';

    this.validators =
      options.validators ?? undefined;

    this.value =
      options.value;

    this.valueField =
      options.valueField ?? undefined;

    this.filterFunction =
      options.filterFunction ?? undefined;

    this.textAlign =
      options.textAlign ?? 'right';
  }

  get isInvalid(): boolean {
    const control: AbstractControl | null =
      this.form().get(this.key);

    return (
      control !== null &&
      control.invalid &&
      (control.dirty || control.touched)
    );
  }

  get maxLengthAttr(): number | null {
    return this.maxLength > 0 ? this.maxLength : null;
  }

  placeholderOrDefault(defaultText: string): string {
    return this.placeholder && this.placeholder !== this.label
      ? this.placeholder
      : defaultText;
  }

  get fieldError(): string {
    const control = this.form().controls[this.key];

    if (!control) {
      return '';
    }

    const hasExternalError =
      this.errors && this.errors.has(this.key);
    const hasEmptyValue = control.value == null || control.value === '';

    if (!hasExternalError && hasEmptyValue && control.pristine && control.untouched) {
      return '';
    }

    if (
      hasExternalError
    ) {
      return this.errors.get(this.key)[0];
    }

    let message =
      'Campo com <strong>valor inesperado</strong>';

    if (!control) {
      return message;
    }

    message =
      control.hasError('required')
        ? `O campo ${this.label} <strong>é obrigatório</strong>`

        : control.hasError('email')
          ? 'Informe um <strong>email válido</strong>'

        : control.hasError('minlength')
          ? `Campo deve ter <strong>mais de ${this.minLength} caracteres</strong>`

        : control.hasError('maxlength')
          ? `Campo deve ter <strong>até ${this.maxLength} caracteres</strong>`

        : control.hasError('min') ||
          control.hasError('max')
          ? this.parseMinMaxMessageError(control)

        : control.hasError('pattern')
          ? `Informe um <strong>${this.label} válido</strong>`

        : control.hasError('alreadyExists')
          ? `Este <strong>${this.label}</strong> já está em uso.`

        : control.hasError('matchControl')
          ? control.getError('matchControl')

        : control.hasError('documentInvalid')
          ? control.getError('documentInvalid')

        : control.hasError('invalid')
          ? control.getError('invalid')

        : '';

    return message;
  }

  formatNumberPtBr(value: number): string {
    return value.toLocaleString('pt-BR');
  }

  parseMinMaxMessageError(
    control: AbstractControl
  ): string {
    if (control.hasError('max')) {
      const maxValue =
        control.getError('max')?.max;

      return `Informe um valor menor ou igual a <strong>${this.formatNumberPtBr(maxValue)}</strong>`;
    }

    if (control.hasError('min')) {
      const minValue =
        control.getError('min')?.min;

      return `Informe um valor maior ou igual a <strong>${this.formatNumberPtBr(minValue)}</strong>`;
    }

    if (
      this.min !== undefined &&
      this.max !== undefined
    ) {
      return `Informe um valor entre <strong>${this.formatNumberPtBr(this.min)} e ${this.formatNumberPtBr(this.max)}</strong>`;
    }

    if (this.min !== undefined) {
      return `Informe um valor maior ou igual a <strong>${this.formatNumberPtBr(this.min)}</strong>`;
    }

    if (this.max !== undefined) {
      return `Informe um valor menor ou igual a <strong>${this.formatNumberPtBr(this.max)}</strong>`;
    }

    return '';
  }

  onNext(event: any): void {
    try {
      event.preventDefault();
      event.stopPropagation();
    } catch {
    }

    this.next.emit(true);
  }
}
